<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">Credit Balance</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`Credits.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/credits/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display v-if="showFilters === true" class="tw-my-3" :filters="filters" />
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4">
      <base-card class="tw-my-3">
        <div class="tw-text-gray-500 tw-text-sm">
          Credits Outstanding
        </div>
        <base-skeleton-loader v-if="isLoading === true" class="tw-w-20 tw-h-6 tw-mt-1" />
        <div v-else class="tw-text-xl tw-font-light tw-my-auto">{{ creditAggregate.balance }}</div>
      </base-card>
      <base-card class="tw-my-3">
        <div class="tw-text-gray-500 tw-text-sm">
          Credits Provisioned
        </div>
        <base-skeleton-loader v-if="isLoading === true" class="tw-w-20 tw-h-6 tw-mt-1" />
        <div v-else class="tw-text-xl tw-font-light tw-my-auto">{{ creditAggregate.provisioned }}</div>
      </base-card>
      <base-card class="tw-my-3">
        <div class="tw-text-gray-500 tw-text-sm">
          Credits Redeemed
        </div>
        <base-skeleton-loader v-if="isLoading === true" class="tw-w-20 tw-h-6 tw-mt-1" />
        <div v-else class="tw-text-xl tw-font-light tw-my-auto">{{ creditAggregate.redeemed }}</div>
      </base-card>
    </div>
    <div class="tw-mt-6">{{ page.total }} credit transactions</div>

    <base-table :loading="isLoading" :fields="fields" :items="credits" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #name="{ item: credit }">
        <base-button type="text" @click="view(credit.userId)">
          <div class="tw-whitespace-nowrap">{{ credit.fullName }}</div>
        </base-button>
      </template>
      <template #plan="{ item: credit }">
        <div class="tw-flex tw-flex-col">
          <div class="tw-whitespace-nowrap">{{ credit.businessPlanName }}</div>
          <div class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500">{{ credit.planGroup }}</div>
        </div>
      </template>
      <template #product="{ item: credit }">
        <div class="tw-whitespace-nowrap">{{ credit.productName }}</div>
      </template>
      <template #amount="{ item: credit }">
        <div class="tw-whitespace-nowrap">{{ credit.creditValue }}</div>
      </template>
      <template #type="{ item: credit }">
        <div class="tw-whitespace-nowrap">{{ credit.creditType }}</div>
      </template>
      <template #provisioned="{ item: credit }">
        <div class="tw-whitespace-nowrap">{{ formatDate(credit.provisionedDate) }}</div>
        <div class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500">{{ credit.planBusinessLocationName }}</div>
      </template>
      <template #redeemed="{ item: credit }">
        <div class="tw-flex tw-flex-col">
          <div class="tw-whitespace-nowrap">{{ formatDate(credit.redeemedDate) }}</div>
          <div class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500">{{ credit.businessRedemptionLocationName }}</div>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      :show-date-filter="false"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :show-plan-status-filter="false"
      :showPatientStateFilter="false"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_revenue_report_take";

export default {
  name: "BusinessCreditsReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    FilterIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      credits: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        locationsFilter: [],
        plansFilter: [],
        planGroupsFilter: [],
      },
      sortKey: "provisioned",
      sortDesc: true,
      creditAggregate: { provisioned: 0, redeemed: 0, balance: 0 },
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    locale() {
      return window.navigator.language;
    },
    showFilters() {
      if (this.filters.locationsFilter.length > 0 || this.filters.plansFilter.length > 0 || this.filters.planGroupsFilter.length > 0) return true;
      else return false;
    },
    fields() {
      let columns = [];
      columns.push({ title: this.organization.customerLabelSingular, key: "name" });
      columns.push({ title: "Plan", key: "plan" });
      columns.push({ title: "Product", key: "product" });
      columns.push({ title: "Amount", key: "amount" });
      columns.push({ title: "Type", key: "type" });
      columns.push({ title: "Provisioned", key: "provisioned", isSortable: true });
      columns.push({ title: "Redeemed", key: "redeemed", isSortable: true });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];
      let planFilterStrings = this.filters.plansFilter
        ? this.filters.plansFilter.map(function(item) {
            return item["businessPlanId"];
          })
        : [];

      return {
        businessLocationIds: locationFilterStrings,
        planGroups: this.filters.planGroupsFilter,
        businessPlanIds: planFilterStrings,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  created() {
    this.loadPage();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    capitalize,
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/credits/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.credits = res.data.page.items;
          this.page.total = res.data.page.total;
          this.creditAggregate.provisioned = res.data.creditAggregate.provisioned;
          this.creditAggregate.redeemed = res.data.creditAggregate.redeemed;
          this.creditAggregate.balance = res.data.creditAggregate.balance;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load credits",
            message: "We had a problem retrieving the list of credits. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      if (!date) return "";
      return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/history`, "_blank");
    },
  },
};
</script>
